<template>
  <section v-reveal class="card p-5 sm:p-6 xl:p-8 w-full mb-6">
    <div
      v-for="(group, gi) in groups"
      :key="group.title"
      :class="gi > 0 ? 'mt-10' : ''"
    >
      <!-- Header -->
      <div class="flex items-center justify-between gap-4 mb-5 pb-4 border-b border-gray-100">
        <div class="section-heading">
          <h2 class="section-title text-2xl">{{ group.title }}</h2>
          <span class="section-subtitle">{{ group.subtitle }}</span>
        </div>
        <router-link to="/artists" class="hidden sm:inline-flex link-arrow shrink-0">
          <span>View All</span>
          <i class="fa-solid fa-arrow-right"></i>
        </router-link>
      </div>

      <!-- Loading skeleton -->
      <div v-if="loading" class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        <div v-for="n in 5" :key="n" class="animate-pulse overflow-hidden rounded-md border border-gray-100" :class="n > 2 ? 'hidden sm:block' : ''">
          <div class="aspect-[3/4] bg-gray-100"></div>
          <div class="space-y-2 p-3">
            <div class="h-3 w-3/4 rounded bg-gray-100"></div>
            <div class="h-2.5 w-1/2 rounded bg-gray-100"></div>
          </div>
        </div>
      </div>

      <!-- Artists Grid Layout -->
      <div v-else class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        <router-link
          v-for="(artist, i) in listFor(group)"
          :key="artist.id"
          v-reveal="{ delay: i * 0.07 }"
          :to="`/profile/${artist.id}`"
          class="group flex flex-col overflow-hidden rounded-md border border-gray-200/70 bg-white transition-shadow duration-300 hover:shadow-card-hover"
        >
          <div class="relative aspect-[3/4] overflow-hidden bg-gray-100">
            <img
              :src="artist.image"
              :alt="artist.name"
              class="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              @error="handleImageError"
            />
            <div class="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/35 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
          </div>

          <div class="flex flex-1 items-start justify-between gap-2 p-3">
            <div class="min-w-0">
              <h3 class="line-clamp-2 font-serif text-base font-bold leading-snug text-gray-900 transition-colors group-hover:text-brand-red">
                {{ artist.name }}
              </h3>
              <p v-if="artist.profession" class="truncate text-xs font-medium text-brand-red-dark/80">
                {{ artist.profession }}
              </p>
            </div>
            <i class="fa-solid fa-arrow-right mt-1.5 text-xs text-gray-300 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-brand-red"></i>
          </div>
        </router-link>

        <!-- Filler tile: invites artists to join / links to the full list -->
        <router-link
          v-reveal="{ delay: listFor(group).length * 0.07 }"
          :to="group.tile === 'join' ? joinLink : '/artists'"
          :class="[
            fillerSpan(listFor(group).length),
            'group flex min-h-[180px] flex-col items-center justify-center gap-2.5 sm:gap-3 rounded-md border border-dashed border-brand-cream-dark bg-brand-cream/30 p-4 sm:p-6 text-center transition-colors duration-300 hover:border-brand-red/40 hover:bg-brand-cream/50',
          ]"
        >
          <template v-if="group.tile === 'join'">
            <span class="flex h-11 w-11 items-center justify-center rounded-md bg-white text-lg text-brand-red shadow-sm">
              <i class="fa-solid fa-palette"></i>
            </span>
            <p class="font-serif text-base sm:text-lg font-bold leading-snug text-brand-red">শিল্পীয়ে নিজৰ পৰিচয় প্ৰকাশ কৰক</p>
            <p class="hidden sm:block max-w-xs text-sm text-gray-600">Are you an artist? Share your profile with our readers.</p>
            <span class="link-arrow mt-1 whitespace-nowrap">
              <span>Join as artist</span>
              <i class="fa-solid fa-arrow-right"></i>
            </span>
          </template>
          <template v-else>
            <span class="flex h-11 w-11 items-center justify-center rounded-md bg-white text-lg text-brand-red shadow-sm">
              <i class="fa-solid fa-users"></i>
            </span>
            <p class="font-serif text-base sm:text-lg font-bold leading-snug text-brand-red">সকলো শিল্পী</p>
            <span class="link-arrow whitespace-nowrap">
              <span>View All Artists</span>
              <i class="fa-solid fa-arrow-right"></i>
            </span>
          </template>
        </router-link>
      </div>

      <div class="sm:hidden pt-4 flex justify-center" v-if="!loading && artists.length > 0 && group.tile === 'join'">
        <router-link to="/artists" class="btn w-full bg-red-50 text-brand-red hover:bg-red-100">
          <span>View All Artists</span>
          <i class="fa-solid fa-arrow-right text-xs"></i>
        </router-link>
      </div>

      <!-- Error -->
      <p v-if="artistError" class="text-brand-red-dark font-medium text-sm mt-4 text-center bg-red-50 p-4 rounded-md border border-red-100">
        {{ artistError }}
      </p>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL;
const FILE_BASE_URL = process.env.VUE_APP_FILE_BASE_URL;

const artists = ref([]);
const loading = ref(true);
const artistError = ref("");

const groups = [
  // Artist Profile: self-published by artists. Renowned Artist: published by the Dapon team (admin).
  { title: 'Artist Profile', subtitle: '( শিল্পীসকলক জানো আহক )', tile: 'join', renowned: false },
  { title: 'Renowned Artist', subtitle: '( স্বনামধন‍্য শিল্পী (দাপোণ গোষ্ঠীৰ দ্বাৰা প্ৰকাশিত) )', tile: 'all', renowned: true },
];

// First five artists of each section
const listFor = (group) => artists.value.filter(a => !!a.is_renowned === group.renowned).slice(0, 5);

// Logged-in users go straight to creating their artist profile
const joinLink = computed(() => (localStorage.getItem('token') ? '/artist' : '/login'));

// The filler tile spans whatever is left of the last row (2 / 3 / 5 columns)
const baseSpan = { 1: 'col-span-1', 2: 'col-span-2' };
const smSpan = { 1: 'sm:col-span-1', 2: 'sm:col-span-2', 3: 'sm:col-span-3' };
const lgSpan = { 1: 'lg:col-span-1', 2: 'lg:col-span-2', 3: 'lg:col-span-3', 4: 'lg:col-span-4', 5: 'lg:col-span-5' };
const fillerSpan = (n) => {
  const base = (2 - (n % 2)) % 2 || 2;
  const sm = (3 - (n % 3)) % 3 || 3;
  const lg = 5 - n;
  return [baseSpan[base], smSpan[sm], lg === 0 ? 'lg:hidden' : `lg:flex ${lgSpan[lg]}`].join(' ');
};

const resolveImage = (img) => {
  if (!img) return `${FILE_BASE_URL}/images/default.png`;
  if (typeof img === "string" && img.startsWith("data:")) return img;
  if (typeof img === "string" && (img.startsWith("http://") || img.startsWith("https://"))) return img;
  if (img instanceof Blob) return URL.createObjectURL(img);
  if (typeof img === "string") return `${FILE_BASE_URL}${img}`;
  return `${FILE_BASE_URL}/images/default.png`;
};

const fetchArtists = async () => {
  const url = `${API_BASE_URL}/artists`;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const approvedArtists = data.filter(a => a.is_approved);
    artists.value = approvedArtists.map((a) => ({
      ...a,
      image: resolveImage(a.image),
    }));
    artistError.value = "";
  } catch (err) {
    artistError.value = "Failed to load artists.";
  } finally {
    loading.value = false;
  }
};

const handleImageError = (e) => {
  const img = e.target;
  if (img.src.includes("default.png")) {
    img.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMjQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSIjOTk5Ij5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=";
    return;
  }
  img.src = `${FILE_BASE_URL}/images/default.png`;
  img.onerror = null;
};

onMounted(() => {
  fetchArtists();
});
</script>
